/**
 * Iter 67en — Public FAQ page at /faq.
 *
 * Renders the published FAQ entries in the visitor's current i18n
 * locale, ordered by chat-question popularity (most-asked first). Each
 * entry is a collapsible accordion. Falls back to English silently
 * when a translation is missing.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BookOpen, ChevronDown, ChevronUp, MessageCircle } from "lucide-react";

export default function Faq() {
  const { i18n } = useTranslation();
  const locale = (i18n.language || "en").split("-")[0];
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/faqs", { params: { locale, limit: 60 } })
      .then(({ data: res }) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setData({ faqs: [] }); });
    return () => { cancelled = true; };
  }, [locale]);

  const faqs = data?.faqs || [];

  /**
   * Iter 67en — FAQPage JSON-LD for Google rich-result accordions.
   * Emitted only when we have ≥1 published FAQ to avoid an empty
   * mainEntity array (Google flags those as errors in Search Console).
   * Strips markdown asterisks from answers so the rich result reads
   * cleanly (Google does NOT render markdown inside the accordion).
   */
  const ldJson = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "inLanguage": locale,
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": (f.answer || "").replace(/\*\*/g, "").trim(),
      },
    })),
  } : null;

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <Header />
      {ldJson && (
        // eslint-disable-next-line react/no-danger
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      )}
      <main className="flex-1 max-w-3xl mx-auto px-4 py-10 w-full" data-testid="faq-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Frequently asked questions</h1>
            <p className="text-sm text-neutral-500 mt-1">Answers to the most common questions from RentalCar.al travelers.</p>
          </div>
        </div>

        {data === null ? (
          <div className="text-sm text-neutral-500">Loading…</div>
        ) : faqs.length === 0 ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900 inline-flex items-start gap-3">
            <MessageCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              No FAQs published yet — but our AI assistant in the bottom-right corner is happy to answer anything.
            </div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="faq-list">
            {faqs.map((f) => {
              const open = openId === f.id;
              return (
                <li key={f.id} className="rounded-2xl border border-neutral-200 bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : f.id)}
                    className="w-full text-left px-4 py-3 flex items-start gap-3 group"
                    aria-expanded={open}
                    data-testid={`faq-row-${f.id}`}
                  >
                    <span className="flex-1 font-display text-base sm:text-lg font-semibold text-neutral-900 group-hover:text-amber-700 transition">
                      {f.question}
                    </span>
                    {open
                      ? <ChevronUp className="h-4 w-4 text-neutral-400 mt-1.5 shrink-0" />
                      : <ChevronDown className="h-4 w-4 text-neutral-400 mt-1.5 shrink-0" />}
                  </button>
                  {open && (
                    <div className="px-4 pb-4 text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap" data-testid={`faq-answer-${f.id}`}>
                      {f.answer}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}
